import React from 'react';
import { ColumnsType } from 'antd/es/table';
import { ArrowDown } from 'lucide-react';
import dayjs from '@/utils/dayjs';
import { formatToPrice } from '@/helpers/utils';
import { convertCurrency } from '@/helpers/convertCurrency';
import { InvoiceV2 } from '@/store/invoice/useGetInvoicesV2';
import { Charge } from '@/store/invoice/useGetCharge';
import { StatusTag } from './_components/StatusTag';
import { BarcodeCell } from './_components/BarcodeCell';
import { PdfCell } from './_components/PdfCell';
import * as S from './styled';

const formatDate = (date?: string) =>
    date ? <S.Tabular>{dayjs(date).format('DD/MM/YYYY')}</S.Tabular> : '—';

const formatMoney = (cents: number) => (
    <S.Money>{formatToPrice(convertCurrency('', cents))}</S.Money>
);

const SHOPMANAGER_COLUMN_KEYS = ['shopmanagerId', 'email'];

export const withoutShopmanagerColumns = <Row,>(
    columns: ColumnsType<Row>,
): ColumnsType<Row> =>
    columns.filter(
        (column) => !SHOPMANAGER_COLUMN_KEYS.includes(String(column.key)),
    );

export const invoicesColumns: ColumnsType<InvoiceV2> = [
    {
        title: 'ID lojista',
        key: 'shopmanagerId',
        render: (_, row) => row.shopmanager?.id,
    },
    {
        title: 'CPF/CNPJ',
        dataIndex: 'doc',
        key: 'doc',
        render: (doc: string) => <S.Tabular>{doc}</S.Tabular>,
    },
    {
        title: 'E-mail lojista',
        key: 'email',
        render: (_, row) => row.shopmanager?.user?.email,
    },
    {
        title: 'Data de emissão',
        dataIndex: 'issuedAt',
        key: 'issuedAt',
        render: formatDate,
    },
    {
        title: (
            <S.DueDateHeader>
                Data de vencimento
                <ArrowDown size={12} strokeWidth={2.5} />
            </S.DueDateHeader>
        ),
        dataIndex: 'dueDate',
        key: 'dueDate',
        render: formatDate,
    },
    {
        title: <S.RightHeader>Valor</S.RightHeader>,
        dataIndex: 'value',
        key: 'value',
        align: 'right',
        render: formatMoney,
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (_, row) => <StatusTag status={row.status} />,
    },
    {
        title: 'Código de barras',
        dataIndex: 'barcode',
        key: 'barcode',
        width: 320,
        render: (_, row) => (
            <BarcodeCell barcode={row.barcode} status={row.status} />
        ),
    },
    {
        title: 'Boleto PDF',
        key: 'pdf',
        align: 'center',
        render: (_, row) => <PdfCell barcode={row.barcode} code={row.code} />,
    },
];

export const chargesColumns: ColumnsType<Charge> = [
    {
        title: 'ID lojista',
        key: 'shopmanagerId',
        render: (_, row) => row.store?.shopmanager_id,
    },
    {
        title: 'E-mail lojista',
        key: 'email',
        render: (_, row) => row.store?.shopManager?.user?.email,
    },
    {
        title: 'Loja',
        key: 'store',
        render: (_, row) => row.store?.name,
    },
    {
        title: 'Mês',
        dataIndex: 'reference_date',
        key: 'reference_date',
        render: (referenceDate: string) =>
            referenceDate ? (
                <S.Capitalized>
                    {dayjs(referenceDate).format('MMMM')}
                </S.Capitalized>
            ) : (
                '—'
            ),
    },
    {
        title: <S.RightHeader>Valor cobrado automaticamente</S.RightHeader>,
        dataIndex: 'automatic_monthly_fee',
        key: 'automatic_monthly_fee',
        align: 'right',
        render: formatMoney,
    },
    {
        title: <S.RightHeader>Valor a ser pago via boleto</S.RightHeader>,
        dataIndex: 'monthly_invoice',
        key: 'monthly_invoice',
        align: 'right',
        render: formatMoney,
    },
];
